/**
 * Placing a deal — or one interest row of it — with an investor.
 *
 * Two granularities, because that is how the work actually happens: a
 * whole deal goes to an investor, or one tract of a multi-tract deal
 * does. Buyers already work this way (each estimator row carries a
 * `buyerId`, and the deal-level `dispositionBuyerId` is derived from
 * the rows), and investors follow the same shape so the two cannot
 * drift into two different mental models on one screen.
 *
 * What lives where:
 *   - rows  — `investorId` + `investorEntityId`, per interest row.
 *   - deal  — the same pair, derived from the rows, plus the capital
 *             placed and the date it went in.
 *
 * Capital is DERIVED, not typed. It used to be a number somebody keyed
 * into the placement strip, which made it a second opinion about the
 * deal's price sitting beside the rows that actually set it — and the
 * two drifted the moment anybody re-priced a tract. What an investor
 * puts in is what the interests sell for, so it is read off the rows:
 * see capitalPlacedFromRows below.
 *
 * Everything here is pure. The database has the final say — the
 * composite foreign key on (investor_entity_id, investor_id) and the
 * capital > 0 check are what actually hold the line — and these
 * functions exist so a user sees a sentence instead of a constraint
 * name, and so the roll-up cannot hand the database a pair it will
 * refuse.
 */
import {
  INVESTOR_SELL_DISCOUNT_PCT, computeEstimatorRow, rowSellsToInvestor,
} from './interestEstimator.js';

/** A row that is sold or ignored is out of the deal and does not vote. */
export function rowCountsForPlacement(row) {
  return !!row && !row.sold && !row.ignored;
}

/**
 * The entities offered when placing with `investorId`.
 *
 * Inactive entities are left out: an entity is marked inactive rather
 * than deleted precisely because it may be named on a recorded deed,
 * and an old one should not be selectable for new work. One already
 * chosen stays in the list, though — otherwise reopening a deal placed
 * through a since-retired entity would show an empty dropdown and look
 * like the placement had been lost.
 */
export function entityOptionsFor(entities, investorId, { keepId = '' } = {}) {
  if (!investorId) return [];
  return (entities || [])
    .filter((e) => e && e.investorId === investorId)
    .filter((e) => e.active !== false || (keepId && e.id === keepId))
    .slice()
    .sort((a, b) => String(a.legalName || '').localeCompare(String(b.legalName || '')));
}

/**
 * Does this entity belong to this investor?
 *
 * The composite foreign key asks the same question at write time. Ask
 * it here so a stale dropdown — the investor changed, the entity did
 * not — is caught before the write rather than after.
 */
export function entityBelongsToInvestor(entities, entityId, investorId) {
  if (!entityId) return false;
  const hit = (entities || []).find((e) => e && e.id === entityId);
  return !!hit && hit.investorId === investorId;
}

function mostCommon(values) {
  const counts = new Map();
  for (const value of values) {
    const key = String(value || '').trim();
    if (!key) continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  let best = '';
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount) { best = value; bestCount = count; }
  }
  return { value: best, count: bestCount, distinct: counts.size };
}

/**
 * Roll the rows up to the deal.
 *
 * Returns `{ investorId, investorEntityId, conflict }`. Both ids or
 * neither — never an investor with no entity, because the deal-level
 * pair is what the deed generator and the fee accrual read, and half a
 * pair is a deal that looks placed and cannot close.
 *
 * `conflict` is true when the active rows name more than one investor.
 * The winner is still returned (the card has to say something), but the
 * caller is expected to show it rather than quietly picking a side:
 * unlike a buyer, an investor decides who takes title.
 *
 * The entity is the most common one AMONG THE ROWS CARRYING THE WINNING
 * INVESTOR — not the most common overall. Counting entities globally
 * could pair investor A with investor B's entity, which is exactly the
 * pair the composite foreign key refuses.
 */
/**
 * Which investor the rows NAME, whether or not an entity is chosen yet.
 *
 * Distinct from derivePlacementFromRows on purpose. A placement needs
 * both ids: the deed names the entity, and the fee and the commission
 * key on a completed placement. But "who is this deal going to" is
 * answered the moment somebody picks the investor — and until it is
 * answered, the card goes on showing the buyer this deal is no longer
 * being sold to.
 *
 * So: intent drives what the card says and clears the stale buyer;
 * placement drives title and money. Returns '' when no live row names
 * an investor.
 */
export function investorIntentFromRows(rows) {
  const active = (Array.isArray(rows) ? rows : []).filter(rowCountsForPlacement);
  return mostCommon(active.map((row) => row.investorId)).value;
}

export function derivePlacementFromRows(rows) {
  const active = (Array.isArray(rows) ? rows : []).filter(rowCountsForPlacement);
  const investor = mostCommon(active.map((row) => row.investorId));
  if (!investor.value) {
    return { investorId: null, investorEntityId: null, conflict: false };
  }
  const entity = mostCommon(
    active.filter((row) => String(row.investorId || '').trim() === investor.value)
      .map((row) => row.investorEntityId),
  );
  if (!entity.value) {
    // An investor named on a row with no entity chosen is not yet a
    // placement — the deed has nothing to name. Leave the deal
    // unplaced rather than writing half a pair.
    return { investorId: null, investorEntityId: null, conflict: investor.distinct > 1 };
  }
  return {
    investorId: investor.value,
    investorEntityId: entity.value,
    conflict: investor.distinct > 1,
  };
}

/**
 * What to patch onto the deal so it matches its rows.
 *
 * Returns only the keys that actually change, in the same spirit as
 * the county/state/buyer roll-up it sits beside: an unchanged key left
 * in the patch is a write, an audit event, and a realtime broadcast
 * for nothing.
 */
export function investorPatchFromRows(rows, deal) {
  const next = derivePlacementFromRows(rows);
  const patch = {};
  const currentInvestor = deal?.investorId || null;
  const currentEntity = deal?.investorEntityId || null;
  // The INVESTOR follows intent, the ENTITY follows the placement.
  //
  // These were one value, and that is what broke the sell-side
  // commission on every investor deal on the book. derivePlacementFromRows
  // returns a null investor until an entity is chosen too — correct for
  // the deed, which cannot name a grantee without one — but it also meant
  // deal.investorId was never written at all, since no investor entity
  // exists yet. Downstream, every rule keyed on "is this an investor
  // deal" read a null and said no, so the rep share never fired and the
  // old buyer's sell-side agent kept being credited.
  //
  // The composite FK is (investor_entity_id, investor_id) MATCH SIMPLE,
  // so a NULL entity satisfies it: writing the investor alone is legal.
  const intendedInvestor = investorIntentFromRows(rows) || null;
  if (intendedInvestor !== currentInvestor) patch.investorId = intendedInvestor;
  // derivePlacementFromRows picks the investor with the same majority
  // vote investorIntentFromRows does, so next.investorId is either null
  // or that same investor — never a different one. The entity it returns
  // therefore always belongs to the investor being written, and a pair
  // the composite FK would reject cannot be formed here. Dropping the
  // investor from the rows nulls next.investorEntityId with it.
  if (next.investorEntityId !== currentEntity) patch.investorEntityId = next.investorEntityId;
  // The deal-level buyer goes when the rows name an investor AND no
  // live row still names a buyer.
  //
  // On INTENT rather than on a completed placement, because the buyer
  // is wrong the moment the decision is made and the entity often
  // lands days later. Left set it is not merely a stale label:
  // dispositionBuyerId is what the card reads, what the
  // Paraclete-self-purchase test reads, and what pairs a sell-side
  // agent onto the deal, so a deal moved to an investor went on
  // crediting the buyer's agent.
  //
  // The second half of the condition is what makes it safe on a
  // genuinely SPLIT deal — one tract to an investor, another still
  // going to a buyer. There is one such deal in production today, and
  // clearing its buyer would have lost the half of the deal nobody
  // moved. A live buyer row means the deal still has a buyer, and the
  // county/state roll-up beside this one will set it from those rows.
  const stillHasBuyerRow = (Array.isArray(rows) ? rows : [])
    .some((row) => rowCountsForPlacement(row) && String(row.buyerId || '').trim());
  if (investorIntentFromRows(rows) && !stillHasBuyerRow && deal?.dispositionBuyerId) {
    patch.dispositionBuyerId = null;
  }
  // Capital follows the pricing, on every estimator save.
  //
  // It was typed into the placement strip, which meant re-pricing a
  // tract left the recorded position behind — and nothing on screen
  // said so, because the strip is opened once and the rows are worked
  // for weeks. The AUM roll-up and the management-fee base both read
  // this field, so a stale number is not cosmetic.
  //
  // Only on a deal that has an investor, so an ordinary buyer deal
  // never grows one; and only when it changes, like every other key
  // here.
  const capitalInvestor = intendedInvestor || currentInvestor;
  if (capitalInvestor) {
    const capital = capitalPlacedFromRows(rows, capitalInvestor);
    const currentCapital = deal?.investorCapital == null ? null : Number(deal.investorCapital);
    if (capital !== currentCapital) patch.investorCapital = capital;
  }
  return patch;
}

/**
 * What the investor is putting in — read off the rows, never typed.
 *
 * It is the SELL total: what the interests go out at, which is what the
 * investor's money buys. Not the buy total, which is Paraclete's basis
 * and is the other half of the margin.
 *
 * On a deal split between parties, only the rows actually placed with
 * this investor count. A deal with one tract to an investor and another
 * still going to a buyer would otherwise report the whole deal as that
 * investor's position, which is wrong everywhere it is read — the AUM
 * roll-up and the management-fee base most of all. With no investorId
 * given, or none named on any row, it is the whole deal, which is the
 * ordinary case: one investor takes the lot.
 *
 * Either way the figure is the DISCOUNTED price: an investor buys under
 * the quoted sell rate, and what they wire is what each placed row
 * prices at — a negotiated figure where one was typed, the default
 * where none was.
 */
export function capitalPlacedFromRows(rows, investorId = '') {
  const active = (Array.isArray(rows) ? rows : []).filter(rowCountsForPlacement);
  const wanted = String(investorId || '').trim();
  const mine = wanted
    ? active.filter((row) => String(row?.investorId || '').trim() === wanted)
    : [];
  const counted = mine.length ? mine : active;
  const total = counted.reduce((sum, row) => {
    const computed = computeEstimatorRow(row);
    if (computed.excluded) return sum;
    const sellTotal = Number(computed.sellTotal) || 0;
    // The INVESTOR'S price, on every row counted.
    //
    // A row that names an investor already carries the 15% discount —
    // it is in the row's price, not in a note applied afterwards — so
    // its sell total is what they wire. A row that does NOT name one,
    // on a deal being placed with an investor, has only ever been
    // priced to the open market, and counting it at that rate would
    // record the quote rather than the cheque.
    //
    // That is the case below: a deal whose investor sits at the deal
    // level with nothing on the rows. Rare, and overstated by nearly
    // 18% every time it happened.
    const atOpenMarket = wanted && !rowSellsToInvestor(row);
    return sum + (atOpenMarket ? sellTotal * (1 - INVESTOR_SELL_DISCOUNT_PCT) : sellTotal);
  }, 0);
  // Whole dollars. A placement is a wire, and a cent on it is noise that
  // makes two readings of the same deal look like different numbers.
  const rounded = Math.round(total);
  // Null rather than 0 for an unpriced deal: the database checks capital
  // > 0, and "nothing priced yet" is not a $0 position.
  return rounded > 0 ? rounded : null;
}

/**
 * The same rules the database holds, said in English.
 *
 * Mirrors deals_investor_capital_positive and deals_investor_entity_fk.
 * Returns null when the placement is fine.
 */
export function placementProblem(placement, entities) {
  const investorId = placement?.investorId || '';
  const entityId = placement?.investorEntityId || '';
  if (!investorId && !entityId) {
    // Not placed. Capital without a placement is a number attached to
    // nobody, so it is refused rather than stored and forgotten.
    const capital = placement?.investorCapital;
    if (capital !== null && capital !== undefined && String(capital).trim() !== '') {
      return 'Choose an investor before recording capital placed.';
    }
    return null;
  }
  if (!investorId) return 'Choose the investor before the entity.';
  if (!entityId) return 'Choose the entity that will take title — the deed names the entity, not the investor.';
  if (!entityBelongsToInvestor(entities, entityId, investorId)) {
    return 'That entity belongs to a different investor. Pick one of this investor’s entities.';
  }
  // Capital is read off the sell rates now rather than typed, so a bad
  // value is a pricing gap rather than a typo — and the sentence says
  // where to go and fix it instead of asking for a number nobody enters
  // any more.
  const raw = placement?.investorCapital;
  if (raw !== null && raw !== undefined && String(raw).trim() !== '') {
    const capital = Number(raw);
    if (!Number.isFinite(capital) || capital <= 0) {
      return 'Capital placed comes from the sell totals on the interest rows, and they add up to $0. Price the rows first.';
    }
  }
  return null;
}

/**
 * Set one buyer, or one investor, on every row of a deal at once.
 *
 * Most deals go to one party. Setting that on twenty rows one dropdown
 * at a time is the kind of work that gets done nineteen times, and the
 * twentieth row then quietly changes what the deal-level roll-up says.
 *
 * Returns { patches, overwrites, skipped }:
 *   patches    — [{ rowId, patch }], only for rows that actually change
 *   overwrites — rows that already named a DIFFERENT party, so the
 *                caller can confirm rather than silently discarding a
 *                deliberate split
 *   skipped    — rows not eligible (sold, ignored), counted so the UI
 *                can say "12 of 15" instead of appearing to miss some
 *
 * `choice` is { kind: 'buyer', buyerId, buyerName } or
 * { kind: 'investor', investorId, investorEntityId }. Either way the
 * other side is cleared on every patched row: a row goes to a buyer or
 * to an investor, never both, and a value left behind would go on
 * voting in the roll-up it is hidden from.
 */
export function planBulkDisposition({ rows, choice } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const patches = [];
  const overwrites = [];
  let skipped = 0;
  const kind = choice?.kind;
  if (kind !== 'buyer' && kind !== 'investor') {
    return { patches: [], overwrites: [], skipped: 0 };
  }
  for (const row of list) {
    if (!rowCountsForPlacement(row)) { skipped += 1; continue; }
    const currentBuyer = String(row.buyerId || '').trim();
    const currentInvestor = String(row.investorId || '').trim();
    let patch = null;
    if (kind === 'buyer') {
      const buyerId = String(choice.buyerId || '').trim();
      if (!buyerId) continue;
      if (currentBuyer === buyerId && !currentInvestor) continue;
      if ((currentBuyer && currentBuyer !== buyerId) || currentInvestor) {
        overwrites.push(row.id);
      }
      patch = {
        buyerId,
        buyerName: choice.buyerName || '',
        investorId: null,
        investorEntityId: null,
      };
    } else {
      const investorId = String(choice.investorId || '').trim();
      const entityId = String(choice.investorEntityId || '').trim();
      // Both or neither — half a placement reads as placed and cannot
      // close, and the composite foreign key refuses it anyway.
      if (!investorId || !entityId) continue;
      const sameAlready = currentInvestor === investorId
        && String(row.investorEntityId || '').trim() === entityId
        && !currentBuyer;
      if (sameAlready) continue;
      if ((currentInvestor && currentInvestor !== investorId) || currentBuyer) {
        overwrites.push(row.id);
      }
      patch = {
        investorId,
        investorEntityId: entityId,
        buyerId: null,
        buyerName: '',
      };
    }
    patches.push({ rowId: row.id, patch });
  }
  return { patches, overwrites, skipped };
}

/** Is this deal placed with an investor? Both ids, or it is not. */
export function isInvestorPlaced(deal) {
  return !!(deal?.investorId && deal?.investorEntityId);
}
